// Styles
import { concatStyleSets, memoizeFunction, mergeStyles } from "@fluentui/react";
import { highContrastSelectionText } from "../../../utilities/HighContrast.styles";
import { backgroundColorHover, dialogBackgroundPrimary, flexChildSizeFixed, flexChildSizeFlexible, flexParent, fontColorNeutralPrimary, fontColorNeutralSecondary, styleButtonAsDiv, styleScrollbar } from "../../../utilities/Mixin.styles";
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { LoopBorderRadius } from "../../../constants/StyleConstants";
// Utilities
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
import { numberToPx, pxToNumber } from "@ms/uno-utilities/lib/local/StyleUtilities";
import { isLoopTheme } from "../../../utilities/ThemingUtilities";
/**
 * Assignment Picker Style constants
 */ const calloutVerticalPadding = "16px";
const calloutHorizontalPadding = "15px";
const assignCalloutWidth = "288px";
const assignCalloutWidthLoop = "263px";
const personaDetailsCalloutSpace = "98px"; // Spacing between persona details and callout edge
const textFieldPaddingBottom = "8px";
const sectionTitlePaddingBottom = "8px";
const userPadding = "4px 8px";
const userHeight = "40px";
const userListSectionFontSize = "14px";
const unassignedSectionPaddingTop = "16px";
/**
 * Helper to get styles for the task assignment control
 * @param theme App context theme
 * @param customStyles Any custom style overrides
 * @param isReadonly Whether the user can edit assignments
 */ export const getStyles = memoizeFunction((theme, customStyles, isReadonly)=>{
    const isLoop = isLoopTheme(theme);
    const AssignmentPickerStyles = {
        root: [
            fontColorNeutralPrimary(theme),
            dialogBackgroundPrimary(theme),
            {
                width: isLoop ? assignCalloutWidthLoop : assignCalloutWidth,
                ".ms-Callout-main": [
                    dialogBackgroundPrimary(theme),
                    {
                        borderRadius: isLoop ? LoopBorderRadius : "initial",
                        paddingLeft: calloutHorizontalPadding,
                        paddingRight: calloutHorizontalPadding,
                        paddingTop: calloutVerticalPadding,
                        paddingBottom: calloutVerticalPadding
                    }
                ],
                ".ms-Persona-primaryText": [
                    fontColorNeutralPrimary(theme),
                    {
                        "&:hover, &:focus, &:focus-within": [
                            backgroundColorHover(theme),
                            {
                                color: fontColorNeutralPrimary(theme)
                            }
                        ]
                    }
                ],
                ".ms-Persona-secondaryText": fontColorNeutralSecondary(theme),
                ".ms-TextField": {
                    paddingBottom: textFieldPaddingBottom
                }
            }
        ],
        searchField: {
            "&.is-active input": {
                borderRadius: "3px"
            }
        },
        userLists: [
            styleScrollbar(theme),
            {
                overflowY: "auto",
                overflowX: "hidden",
                "> .userListSection:nth-child(2)": {
                    paddingTop: unassignedSectionPaddingTop
                },
                ".sectionTitle": {
                    fontSize: userListSectionFontSize,
                    fontWeight: 700,
                    paddingBottom: sectionTitlePaddingBottom
                },
                ".sectionText": {
                    fontSize: userListSectionFontSize,
                    fontWeight: 100
                }
            }
        ],
        user: [
            flexParent(),
            isLoop && {
                borderRadius: LoopBorderRadius
            },
            {
                alignItems: "center",
                padding: userPadding,
                height: userHeight,
                ".ms-Persona": flexChildSizeFlexible("auto"),
                ".personaActionButton": {
                    marginLeft: "8px"
                },
                "> button": [
                    styleButtonAsDiv,
                    {
                        cursor: "pointer"
                    }
                ],
                ".ms-Icon": [
                    flexChildSizeFixed(),
                    {
                        cursor: "pointer",
                        lineHeight: userHeight,
                        fontSize: "16px"
                    }
                ],
                ".ms-Persona-details": {
                    maxWidth: numberToPx(pxToNumber(isLoop ? assignCalloutWidthLoop : assignCalloutWidth) - pxToNumber(personaDetailsCalloutSpace))
                }
            },
            isReadonly && {
                cursor: "default",
                "> button": [
                    {
                        cursor: "default",
                        "> ms-Icon": {
                            cursor: "default"
                        }
                    }
                ]
            },
            !isReadonly && {
                cursor: "pointer",
                "> button": {
                    padding: "2px"
                },
                "&:hover, &:focus, &:focus-within": [
                    backgroundColorHover(theme),
                    highContrastSelectionText(),
                    {
                        ".ms-Persona-details > *, .personaActionButton > .plannerDismiss > span": [
                            theme.name === PlannerTheme.HighContrast && {
                                color: Colors.Black
                            },
                            highContrastSelectionText()
                        ]
                    }
                ]
            }
        ],
        agentOptionButton: {
            width: "100%"
        },
        agentButtonContent: {
            display: "flex",
            ".infoIcon": {
                padding: "4px"
            }
        }
    };
    return concatStyleSets(AssignmentPickerStyles, customStyles);
});
/**
 * Get the classnames associated with the computed styles for the assignment picker component
 * @param styles The styles for the component
 */ export const getClassNames = memoizeFunction((styles)=>{
    return {
        root: mergeStyles("assignmentPicker", styles.root),
        searchField: mergeStyles("searchField", styles.searchField),
        userLists: mergeStyles("userLists", styles.userLists),
        user: mergeStyles("user", styles.user),
        agentButtonContent: mergeStyles("agentButtonContent", styles.agentButtonContent),
        agentOptionButton: mergeStyles("agentOptionButton", styles.agentOptionButton)
    };
});
export const getInfoTooltipStyles = ()=>{
    return {
        gapSpace: 0,
        styles: {
            root: {
                width: 240
            }
        }
    };
};
